/**
 * Level-triggered notification evaluator.
 *
 * Runs on every chats-list emission (poll, refetch, cache write) and decides,
 * from durable server state alone, what to do about each unread chat. There is
 * no event detection here — no diffing of consecutive snapshots, no stream
 * lifecycle callbacks — so a completion observed late (after a reload, a
 * remount, or several missed polls) degrades to a late notification, never a
 * missed one. Idempotency comes from the per-chat watermark.
 */
import { normalizeChatUuid } from './notification-helpers';
import type { GetChatsByFiltersChat } from '../../../queries/chats';

/**
 * Unreads older than this relative to page load never toast (they stay
 * badge/dot only) — returning after a weekend should not replay history.
 * The grace covers completions that landed during the load itself.
 */
const PRELOAD_GRACE_MS = 60_000;

let parentVisibilityState: { visible: boolean; focused: boolean } | null = null;

export function setParentVisibilityState(state: { visible: boolean; focused: boolean } | null): void {
  parentVisibilityState = state;
}

export interface ChatNotificationCandidate {
  /** Normalized chat uuid. */
  chatUuid: string;
  /** The server-side update being handled; becomes the watermark on claim. */
  lastMessageAtMs: number;
  /** Notification body. Chat title until the backend exposes a message preview. */
  body: string;
}

export interface ChatMarkReadCandidate {
  chatUuid: string;
  lastMessageAtMs: number;
}

export interface ChatsEmissionActions {
  /** Unread chats the user is looking at right now: mark read, never toast. */
  markRead: ChatMarkReadCandidate[];
  /** Unread chats to notify for, pending a watermark claim at display time. */
  notify: ChatNotificationCandidate[];
}

export function evaluateChatsEmission({
  chats,
  isTabVisible,
  sessionStartedAtMs,
  nowMs,
  getWatermark,
  isChatActivelyViewed,
}: {
  chats: readonly GetChatsByFiltersChat[];
  /** document is visible AND window focused, sampled at emission time. */
  isTabVisible: boolean;
  sessionStartedAtMs: number;
  nowMs: number;
  getWatermark: (chatUuid: string) => number | undefined;
  isChatActivelyViewed: (chatUuid: string) => boolean;
}): ChatsEmissionActions {
  const markRead: ChatMarkReadCandidate[] = [];
  const notify: ChatNotificationCandidate[] = [];

  for (const chat of chats) {
    if (!chat.uuid || !chat.hasUnreadMessages) {
      continue;
    }

    // A row with a live stream is mid-turn: the unread state belongs to a
    // message that is still being written (or to the user's own send that
    // started the turn). Level-triggering means we simply handle it on the
    // emission where the stream is gone and the row is still unread.
    if (chat.activeStreamId) {
      continue;
    }

    const chatUuid = normalizeChatUuid(chat.uuid);
    // Prefer the assistant-scoped timestamp when the backend provides it:
    // lastMessageAt is also bumped by the user's own sends, which must not
    // mint a new dedupe key. The fields land in the selection set once the
    // backend schema ships; until then they are undefined and we fall back.
    const assistantFields = chat as {
      lastAssistantMessageAt?: string | null;
      lastAssistantMessagePreview?: string | null;
    };
    const updateTimestamp = assistantFields.lastAssistantMessageAt ?? chat.lastMessageAt;
    const lastMessageAtMs = updateTimestamp ? Date.parse(updateTimestamp) : Number.NaN;
    if (Number.isNaN(lastMessageAtMs)) {
      // Without a timestamp there is nothing to dedupe on; leave it badge-only.
      continue;
    }

    if (isChatActivelyViewed(chatUuid) && isTabVisible) {
      markRead.push({ chatUuid, lastMessageAtMs });
      continue;
    }

    const watermark = getWatermark(chatUuid);
    if (watermark !== undefined && watermark >= lastMessageAtMs) {
      continue;
    }

    // Future-dated rows (clock skew) are treated as fresh rather than waiting
    // out the skew; the watermark still dedupes them.
    const isFresh = lastMessageAtMs >= sessionStartedAtMs - PRELOAD_GRACE_MS || lastMessageAtMs > nowMs;
    if (!isFresh) {
      continue;
    }

    notify.push({
      chatUuid,
      lastMessageAtMs,
      body: assistantFields.lastAssistantMessagePreview?.trim() || chat.title?.trim() || 'You have a new message',
    });
  }

  return { markRead, notify };
}

/**
 * True only when the tab is rendered AND its window has focus — the only
 * situation where "the chat is on screen" implies the user can actually see it.
 */
export function isTabAndWindowVisible(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.visibilityState === 'visible' && (document.hasFocus() || parentVisibilityState?.focused === true);
}
